import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alarm, ArrowRight, ArrowsClockwise, Buildings, CalendarBlank, CalendarPlus, CaretLeft, CaretRight, Clock, Crown, Link as LinkIcon,
  MicrosoftOutlookLogo, PencilSimple, Plus, Ticket, Trash, User, UserCircle, UsersThree, WarningCircle, Airplane,
} from '@phosphor-icons/react';
import { supabase } from '../../../lib/supabase/client';
import { ladeFristen, baueFristEintraege, type FristEintrag } from '../../../lib/kalender/fristen';
import { ladeTicketsZuTerminen } from '../../../lib/tickets/terminLink';
import { terminTypLabel } from '../../../lib/termine/typLabel';
import { KATEGORIE_INFO, monatsRaster, terminTag } from '../../../lib/immobilien/termine';
import { eintraegeJeTag, kalenderUntertitel, kategorieAnzahl, monatsGrenzen, terminTage, type KalenderEintrag, type KalenderFilter } from '../../../lib/immobilien/kalender';
import { zustaendigFarben, type ZustaendigFarbe } from '../../../lib/emails/bearbeitung';
import {
  abwesenheitenJeTag, jetztStatus, mailVerzeichnis, terminFuerPersonen, terminPersonId, vorname,
  type AbwesenheitEintrag, type AbwesenheitRoh, type KalenderPerson,
} from '../../../lib/kalender/personen';
import { heuteIso } from '../../../lib/immobilien/format';
import { BestaetigenDialog, Chip, ImmoButton } from '../../../components/immobilien/ui';
import { useIslandActions } from '../../../components/island/IslandProvider';
import { usePillenSteuerung } from '../../../components/island/widgets/SteuerungsAnsicht';
import { exportWidget } from '../../../components/island/widgets/exportWidget';
import { ExportColumn } from '../../../lib/export/types';
import { TerminFormularKarte } from '../objekte/gemeinsam/TerminFormularKarte';
import BuchungslinksVerwaltung from './BuchungslinksVerwaltung';

export interface TerminTeilnehmer {
  email: string;
  name?: string | null;
  status?: 'accepted' | 'declined' | 'tentative' | 'none';
}

export interface Termin {
  id: string;
  titel: string;
  typ: string;
  /** Eigene Bezeichnung bei typ = 'sonstig' (sonst null) — siehe lib/termine/typLabel.ts. */
  typ_freitext?: string | null;
  start_datum: string;
  end_datum: string | null;
  beschreibung: string | null;
  ganztaegig: boolean;
  status: string;
  farbe: string | null;
  notizen: string | null;
  objekt_id: string | null;
  mieter_id: string | null;
  eigentuemer_id: string | null;
  zustaendig_id: string | null;
  outlook_event_id?: string | null;
  teilnehmer?: TerminTeilnehmer[] | null;
  /** Mieterportal (Portale E5) */
  fuer_mieter_sichtbar?: boolean;
  fuer_eigentuemer_sichtbar?: boolean;
  mieter_hinweis?: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  objekt?: { id: string; bezeichnung: string; strasse: string; hausnummer: string } | null;
  mieter_rel?: { id: string; vorname: string; nachname: string } | null;
  eigentuemer_rel?: { id: string; vorname: string; nachname: string; firma_name: string | null } | null;
  zustaendig?: { id: string; full_name: string; avatar_url: string | null } | null;
}

interface KalenderViewProps {
  /** Wird nicht mehr gebraucht (Nutzer kommt aus der Sitzung), bleibt für den Aufruf aus App.tsx. */
  userProfile?: { id?: string } | null;
  initialTerminId?: string | null;
  onClearInitial?: () => void;
  onNavigateToObjekt?: (id: string) => void;
  onNavigateToMieter?: (id: string) => void;
  onNavigateToEigentuemer?: (id: string) => void;
  onNavigateToTicket?: (id: string) => void;
  onNavigateToRechnung?: (id: string) => void;
  onNavigateToEmail?: (id: string) => void;
}

const MONATE = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
const MON_KURZ = ['JAN', 'FEB', 'MÄR', 'APR', 'MAI', 'JUN', 'JUL', 'AUG', 'SEP', 'OKT', 'NOV', 'DEZ'];
const WOCHENTAGE = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
const WT_KOPF = ['MO', 'DI', 'MI', 'DO', 'FR', 'SA', 'SO'];
const STATUS_LABELS: Record<string, string> = { geplant: 'Geplant', abgeschlossen: 'Abgeschlossen', abgesagt: 'Abgesagt' };
const FILTER: readonly KalenderFilter[] = ['alle', 'wartung', 'vermietung', 'versammlung', 'ablesung', 'sonstige'];
const FRIST_FARBE = 'var(--bad)';
const FRISTEN_SCHLUESSEL = 'immo_kalender_fristen';

const formatDatum = (d: string | null | undefined): string => {
  if (!d) return '';
  const date = new Date(d);
  return `${String(date.getDate()).padStart(2, '0')}.${String(date.getMonth() + 1).padStart(2, '0')}.${date.getFullYear()}`;
};

const TERMIN_EXPORT_COLUMNS: ExportColumn[] = [
  { key: 'titel', label: 'Titel' },
  { key: 'typ', label: 'Typ' },
  { key: 'start_datum', label: 'Startdatum', format: (v) => formatDatum(v) },
  { key: 'end_datum', label: 'Enddatum', format: (v) => formatDatum(v) },
  { key: 'status', label: 'Status' },
  { key: 'objekt', label: 'Objekt', format: (_, row) => row.objekt?.bezeichnung || '' },
  { key: 'mieter_rel', label: 'Mieter', format: (_, row) => row.mieter_rel ? `${row.mieter_rel.vorname || ''} ${row.mieter_rel.nachname || ''}`.trim() : '' },
  { key: 'eigentuemer_rel', label: 'Eigentümer', format: (_, row) => row.eigentuemer_rel ? (row.eigentuemer_rel.firma_name || `${row.eigentuemer_rel.vorname || ''} ${row.eigentuemer_rel.nachname || ''}`.trim()) : '' },
  { key: 'zustaendig', label: 'Zuständig', format: (_, row) => row.zustaendig?.full_name || '' },
];

const TERMIN_SELECT = '*, objekt:objekte(id, bezeichnung, strasse, hausnummer), mieter_rel:mieter(id, vorname, nachname), eigentuemer_rel:eigentuemer(id, vorname, nachname, firma_name), zustaendig:profiles!termine_zustaendig_id_fkey(id, full_name, avatar_url)';

/** „10:00 – 11:00 Uhr“ | „Ganztägig“ */
const zeitLabel = (t: Termin): string => {
  const tage = terminTage(t.start_datum, t.end_datum, t.ganztaegig);
  if (tage.length > 1) {
    const kurz = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}.`;
    return `${kurz(tage[0]!)} – ${kurz(tage[tage.length - 1]!)} · ${tage.length} Tage`;
  }
  if (t.ganztaegig) return 'Ganztägig';
  const fmt = (iso: string) => { const d = new Date(iso); return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`; };
  return t.end_datum ? `${fmt(t.start_datum)} – ${fmt(t.end_datum)} Uhr` : `${fmt(t.start_datum)} Uhr`;
};

const personName = (p: { vorname?: string | null; nachname?: string | null; firma_name?: string | null } | null | undefined) =>
  p ? ((p.firma_name ?? '').trim() || `${p.vorname ?? ''} ${p.nachname ?? ''}`.trim()) : '';

const TEILNAHME: Record<string, { text: string; farbe: string }> = {
  accepted: { text: 'Zugesagt', farbe: 'var(--ok)' }, declined: { text: 'Abgesagt', farbe: 'var(--bad)' },
  tentative: { text: 'Vorbehalt', farbe: 'var(--warn)' }, none: { text: 'Offen', farbe: 'var(--ink3)' },
};

const PERSONEN_SCHLUESSEL = 'immo_kalender_personen';
function liesPersonenWahl(): Set<string> {
  try {
    const roh = JSON.parse(localStorage.getItem(PERSONEN_SCHLUESSEL) || '[]');
    return new Set(Array.isArray(roh) ? roh.filter((x): x is string => typeof x === 'string') : []);
  } catch { return new Set(); }
}

/** Farben des Stands JETZT im Personenfilter. */
const STATUS_FARBE: Record<'abwesend' | 'termin' | 'frei', string> = {
  abwesend: 'var(--ink3)',
  termin: 'var(--warn, #d97706)',
  frei: 'var(--ok, #16a34a)',
};
const ABWESEND_FLAECHE = 'var(--fill2)';

function liesFristenWahl(): boolean {
  try { return localStorage.getItem(FRISTEN_SCHLUESSEL) !== 'aus'; } catch { return true; }
}

/**
 * Kalender (README §5.7, Screen 40): Monatsansicht über alle Objekte, Filter nach Art,
 * Tagesliste rechts. Erhalten aus der bisherigen Ansicht: Outlook-Abgleich, Termin anlegen
 * (auch an einem Tag), bearbeiten, löschen, Fristen (Rechnung/Ticket/E-Mail), Ticket-Bezug,
 * Teilnehmer, Buchungslinks, Export und Deep-Link auf einen Termin („Im Kalender zeigen“).
 */
const KalenderView: React.FC<KalenderViewProps> = ({
  initialTerminId, onClearInitial, onNavigateToObjekt, onNavigateToMieter, onNavigateToEigentuemer,
  onNavigateToTicket, onNavigateToRechnung, onNavigateToEmail,
}) => {
  const heute = heuteIso();
  const [jahr, setJahr] = useState(Number(heute.slice(0, 4)));
  const [monat, setMonat] = useState(Number(heute.slice(5, 7)));
  const [gewaehlt, setGewaehlt] = useState(heute);
  const [filter, setFilter] = useState<KalenderFilter>('alle');
  const [termine, setTermine] = useState<Termin[]>([]);
  const [danach, setDanach] = useState<Termin[]>([]);
  const [ticketProTermin, setTicketProTermin] = useState<Record<string, { id: string; ticket_nummer: string | null; titel: string | null }>>({});
  const [fristen, setFristen] = useState<FristEintrag[]>([]);
  const [zeigeFristen, setZeigeFristen] = useState<boolean>(liesFristenWahl);
  const [laedt, setLaedt] = useState(true);
  /** Monat, für den `termine` geladen sind (YYYY-MM) – der Deep-Link wartet darauf. */
  const [geladenFuer, setGeladenFuer] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [hervorgehoben, setHervorgehoben] = useState<string | null>(null);
  const [teamMembers, setTeamMembers] = useState<KalenderPerson[]>([]);
  // Farben je Zuständigem wie im Postfach (#615): dieselbe Liste wie dort, damit jede Person dieselbe Farbe hat
  const [farben, setFarben] = useState<Map<string, ZustaendigFarbe>>(new Map());
  // Personenfilter (Ticket #506): leer = alle. Die Wahl merkt sich der Browser.
  const [personenWahl, setPersonenWahl] = useState<Set<string>>(liesPersonenWahl);
  const [abwesenheiten, setAbwesenheiten] = useState<AbwesenheitRoh[]>([]);
  // Stand JETZT je Mitarbeiter („ist Björn gerade im Termin?") — unabhängig vom angezeigten Monat.
  const [heuteTermine, setHeuteTermine] = useState<Termin[]>([]);
  const [heuteAbw, setHeuteAbw] = useState<AbwesenheitRoh[]>([]);
  const [jetzt, setJetzt] = useState(() => new Date());
  const [syncLaeuft, setSyncLaeuft] = useState(false);
  const [loeschen, setLoeschen] = useState<Termin | null>(null);
  const [loeschtGerade, setLoeschtGerade] = useState(false);
  const { openForm, notify } = useIslandActions();
  const liste = useRef<HTMLDivElement>(null);

  const toggleFristen = useCallback(() => setZeigeFristen(v => {
    try { localStorage.setItem(FRISTEN_SCHLUESSEL, v ? 'aus' : 'an'); } catch { /* ohne Speicher gilt die Wahl bis zum Neuladen */ }
    return !v;
  }), []);

  const ladeMonat = useCallback(async () => {
    setLaedt(true);
    setFehler(null);
    const { von, bis } = monatsGrenzen(jahr, monat);
    const [terminQ, fristenRoh, abwQ] = await Promise.all([
      // Auch Termine, die vorher beginnen und in den Monat hineinreichen (Urlaub über den Monatswechsel, #615)
      supabase.from('termine').select(TERMIN_SELECT).lt('start_datum', bis).or(`start_datum.gte.${von},end_datum.gte.${von}`).order('start_datum', { ascending: true }),
      ladeFristen(von, bis).catch((e: unknown) => { console.warn('Fristen laden fehlgeschlagen', e); return null; }),
      // Nur genehmigte (Entscheidung Patrick 16.10.2026); alles, was den Monat berührt
      supabase.from('abwesenheiten').select('id, mitarbeiter_id, typ, von_tag, bis_tag, halber_tag, status')
        .eq('status', 'genehmigt').lt('von_tag', bis).gte('bis_tag', von),
    ]);
    setAbwesenheiten((abwQ.data as AbwesenheitRoh[] | null) ?? []);
    if (terminQ.error) setFehler(terminQ.error.message);
    const geladen = (terminQ.data as unknown as Termin[] | null) ?? [];
    setTermine(geladen);
    ladeTicketsZuTerminen(geladen.map(t => t.id)).then(setTicketProTermin).catch(() => setTicketProTermin({}));
    setFristen(fristenRoh ? baueFristEintraege(fristenRoh, heuteIso()) : []);
    setGeladenFuer(`${jahr}-${String(monat).padStart(2, '0')}`);
    setLaedt(false);
  }, [jahr, monat]);

  useEffect(() => { void ladeMonat(); }, [ladeMonat]);

  useEffect(() => {
    supabase.from('profiles').select('id, full_name, email').in('role', ['admin', 'team', 'super admin']).eq('zuweisbar', true).order('full_name')
      .then(({ data }) => setTeamMembers(((data ?? []) as { id: string; full_name: string | null; email: string | null }[])
        .map(p => ({ id: p.id, full_name: p.full_name ?? '', email: p.email }))));
    Promise.all([
      supabase.from('teams').select('id, name').order('name'),
      supabase.from('profiles').select('id, full_name').or('role.eq.team,role.eq.admin').eq('zuweisbar', true),
    ]).then(([t, m]) => setFarben(zustaendigFarben(
      (t.data ?? []) as { id: string; name: string }[],
      ((m.data ?? []) as { id: string; full_name: string | null }[]).map(p => ({ id: p.id, full_name: p.full_name ?? '' })),
    )));
  }, []);

  // Heute: Termine und Abwesenheiten für den Stand JETZT; nach jeder Änderung neu laden
  useEffect(() => {
    const h = heuteIso();
    const morgen = new Date(`${h}T00:00:00`); morgen.setDate(morgen.getDate() + 1);
    const start = new Date(`${h}T00:00:00`);
    Promise.all([
      supabase.from('termine').select('id, titel, start_datum, end_datum, ganztaegig, status, zustaendig_id, teilnehmer')
        .gte('start_datum', start.toISOString()).lt('start_datum', morgen.toISOString()),
      supabase.from('abwesenheiten').select('id, mitarbeiter_id, typ, von_tag, bis_tag, halber_tag, status')
        .eq('status', 'genehmigt').lte('von_tag', h).gte('bis_tag', h),
    ]).then(([t, a]) => {
      setHeuteTermine((t.data as unknown as Termin[] | null) ?? []);
      setHeuteAbw((a.data as AbwesenheitRoh[] | null) ?? []);
    });
  }, [termine]);
  // Minütlich weiterzählen, damit „im Termin bis 15:30" nicht stehen bleibt
  useEffect(() => { const t = window.setInterval(() => setJetzt(new Date()), 60_000); return () => window.clearInterval(t); }, []);

  const umschaltePerson = useCallback((id: string | null) => setPersonenWahl(alt => {
    const neu = new Set(id === null ? [] : alt);
    if (id !== null) { if (neu.has(id)) neu.delete(id); else neu.add(id); }
    try { localStorage.setItem(PERSONEN_SCHLUESSEL, JSON.stringify([...neu])); } catch { /* gilt bis zum Neuladen */ }
    return neu;
  }), []);

  // „Danach“: die nächsten Termine nach dem gewählten Tag (unabhängig vom Monat), nach Filter
  useEffect(() => {
    let aktiv = true;
    const ab = new Date(`${gewaehlt}T00:00:00`);
    ab.setDate(ab.getDate() + 1);
    supabase.from('termine').select('id, titel, typ, typ_freitext, start_datum, end_datum, ganztaegig, status, objekt_id, zustaendig_id, teilnehmer, objekt:objekte(id, bezeichnung, strasse, hausnummer)')
      .gte('start_datum', ab.toISOString()).neq('status', 'abgesagt').order('start_datum', { ascending: true }).limit(40)
      .then(({ data }) => { if (aktiv) setDanach((data as unknown as Termin[] | null) ?? []); });
    return () => { aktiv = false; };
  }, [gewaehlt, termine]);

  // Deep-Link: Termin wählen und hervorheben; liegt er in einem anderen Monat, dorthin springen
  // und warten, bis genau dieser Monat geladen ist. Gibt es ihn nicht, aufgeben.
  const deepLinkZiel = useRef<{ id: string; monat: string } | null>(null);
  useEffect(() => {
    if (!initialTerminId || laedt || !geladenFuer) return;
    const t = termine.find(x => x.id === initialTerminId);
    if (t) {
      deepLinkZiel.current = null;
      setGewaehlt(terminTag(t.start_datum, t.ganztaegig));
      setFilter('alle');
      setHervorgehoben(t.id);
      onClearInitial?.();
      window.setTimeout(() => document.getElementById(`termin-${t.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 250);
      return;
    }
    const ziel = deepLinkZiel.current;
    if (ziel?.id === initialTerminId) {
      if (ziel.monat === geladenFuer) { deepLinkZiel.current = null; onClearInitial?.(); }
      return;
    }
    deepLinkZiel.current = { id: initialTerminId, monat: '' };
    void (async () => {
      const { data } = await supabase.from('termine').select('id, start_datum, ganztaegig').eq('id', initialTerminId).maybeSingle();
      const z = data as { start_datum: string; ganztaegig: boolean | null } | null;
      if (!z?.start_datum) { deepLinkZiel.current = null; onClearInitial?.(); return; }
      const tag = terminTag(z.start_datum, z.ganztaegig);
      // schon im geladenen Monat und trotzdem nicht gefunden (z. B. gerade gelöscht) → aufgeben
      if (tag.slice(0, 7) === geladenFuer) { deepLinkZiel.current = null; onClearInitial?.(); return; }
      deepLinkZiel.current = { id: initialTerminId, monat: tag.slice(0, 7) };
      setJahr(Number(tag.slice(0, 4)));
      setMonat(Number(tag.slice(5, 7)));
    })();
  }, [initialTerminId, termine, laedt, geladenFuer, onClearInitial]);

  const handleAktualisieren = async () => {
    if (syncLaeuft) return;
    setSyncLaeuft(true);
    try {
      const { data, error } = await supabase.functions.invoke('graph-kalender-sync', { body: {} });
      if (error) throw error;
      const r = (data ?? {}) as { wartet_auf_consent?: boolean; importiert?: number; aktualisiert?: number; exportiert?: number; geloescht_outlook?: number; geloescht_app?: number; fehler?: number };
      if (r.wartet_auf_consent) {
        notify({ title: 'Outlook-Abgleich wartet auf die Azure-Freigabe', tone: 'error' });
      } else {
        const teile: string[] = [];
        if (r.importiert) teile.push(`${r.importiert} neu`);
        if (r.aktualisiert) teile.push(`${r.aktualisiert} aktualisiert`);
        if (r.exportiert) teile.push(`${r.exportiert} nach Outlook`);
        if ((r.geloescht_app || 0) + (r.geloescht_outlook || 0) > 0) teile.push(`${(r.geloescht_app || 0) + (r.geloescht_outlook || 0)} gelöscht`);
        if (r.fehler) teile.push(`${r.fehler} Fehler`);
        notify({ title: 'Outlook-Abgleich fertig', body: teile.length ? teile.join(', ') : 'Alles aktuell', tone: r.fehler ? 'error' : 'success' });
      }
    } catch (e) {
      console.warn('Kalender-Sync-Trigger fehlgeschlagen', e);
      notify({ title: 'Outlook-Abgleich fehlgeschlagen', tone: 'error' });
    }
    await ladeMonat();
    setSyncLaeuft(false);
  };

  // Termin-Formular (gleiche Logik) im neuen Design über dem Kalender
  const [formular, setFormular] = useState<{ termin: Termin | null; prefill: string | null } | null>(null);
  const formularRef = useRef<HTMLDivElement>(null);
  const terminFormular = (termin: Termin | null, prefill: string | null) => {
    setFormular({ termin, prefill });
    window.setTimeout(() => formularRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };

  // Löschen (Tombstone-Trigger löscht in Outlook mit)
  const bestaetigeLoeschen = async () => {
    if (!loeschen) return;
    setLoeschtGerade(true);
    const { error } = await supabase.from('termine').delete().eq('id', loeschen.id);
    setLoeschtGerade(false);
    if (error) { notify({ title: 'Termin konnte nicht gelöscht werden', body: error.message, tone: 'error' }); return; }
    setLoeschen(null);
    notify({ title: 'Termin gelöscht', tone: 'success' });
    void ladeMonat();
  };

  const fristOeffnen = (f: FristEintrag) => {
    if (f.ziel.typ === 'rechnung') onNavigateToRechnung?.(f.ziel.id);
    else if (f.ziel.typ === 'ticket') onNavigateToTicket?.(f.ziel.id);
    else onNavigateToEmail?.(f.ziel.id);
  };

  const [exportAlle, setExportAlle] = useState(false);
  const fetchAllTermine = async () => {
    const { data } = await supabase.from('termine').select(TERMIN_SELECT).order('start_datum', { ascending: true }).limit(10000);
    return data || [];
  };

  const typCounts: Record<string, number> = {};
  termine.forEach(t => { typCounts[t.typ] = (typCounts[t.typ] || 0) + 1; });
  const terminStats = { items: [{ label: 'Gesamt', value: termine.length }, ...Object.entries(typCounts).map(([typ, count]) => ({ label: typ, value: count }))] };

  // Fristen, Buchungslinks und Export als Widgets in der Pillen-Steuerung (wie bisher, Handoff Pille §6)
  usePillenSteuerung({
    titel: 'Kalender',
    eintraege: [
      { id: 'fristen', typ: 'schalter', farbe: 'rot', icon: Alarm, kicker: 'Anzeige', titel: 'Fristen', an: zeigeFristen, onChange: () => toggleFristen(),
        unter: zeigeFristen ? `An${fristen.length ? ` · ${fristen.length}` : ''}` : 'Aus', tooltip: 'Fristen im Kalender anzeigen' },
      { id: 'buchungslinks', typ: 'aktion', farbe: 'blau', icon: LinkIcon, kicker: 'Termine', titel: 'Buchungslinks', unter: 'Links zur Terminbuchung',
        onClick: () => openForm(<BuchungslinksVerwaltung />, 'Buchungslinks', 480, { icon: LinkIcon, farbe: 'blau', badge: 'kalender' }) },
      exportWidget({ id: 'export', title: 'Termine', titel: 'Termine', columns: TERMIN_EXPORT_COLUMNS, data: termine, fetchAllData: fetchAllTermine,
        stats: terminStats, alleDaten: exportAlle, setAlleDaten: setExportAlle, tooltip: 'Termine als CSV, Excel oder PDF herunterladen' }),
    ],
  }, [termine, fristen, zeigeFristen, exportAlle]);

  const idZuMail = useMemo(() => mailVerzeichnis(teamMembers), [teamMembers]);
  const namen = useMemo(() => new Map(teamMembers.map(p => [p.id, p.full_name])), [teamMembers]);
  const termineGefiltert = useMemo(() => termine.filter(t => terminFuerPersonen(t, personenWahl, idZuMail)), [termine, personenWahl, idZuMail]);
  const proTag = useMemo(() => eintraegeJeTag(termineGefiltert, fristen, filter, zeigeFristen), [termineGefiltert, fristen, filter, zeigeFristen]);
  const anzahl = useMemo(() => kategorieAnzahl(termineGefiltert), [termineGefiltert]);
  const abwJeTag = useMemo(() => {
    const { von, bis } = monatsGrenzen(jahr, monat);
    // Das Raster zeigt Randtage der Nachbarmonate — eine Woche Puffer auf beiden Seiten
    const vonP = new Date(`${von}T12:00:00`); vonP.setDate(vonP.getDate() - 7);
    const bisP = new Date(`${bis}T12:00:00`); bisP.setDate(bisP.getDate() + 7);
    return abwesenheitenJeTag(abwesenheiten, namen, personenWahl, vonP.toISOString().slice(0, 10), bisP.toISOString().slice(0, 10));
  }, [abwesenheiten, namen, personenWahl, jahr, monat]);
  const heuteAbwEintraege = useMemo(() => {
    const h = heuteIso();
    return abwesenheitenJeTag(heuteAbw, namen, new Set(), h, `${h}~`).get(h) ?? [];
  }, [heuteAbw, namen]);
  const raster = useMemo(() => monatsRaster(jahr, monat), [jahr, monat]);
  const tagesEintraege = proTag.get(gewaehlt) ?? [];
  const tagesAbwesend: AbwesenheitEintrag[] = abwJeTag.get(gewaehlt) ?? [];
  const kommende = useMemo(() => eintraegeJeTag(danach.filter(t => terminFuerPersonen(t, personenWahl, idZuMail)), [], filter, false), [danach, filter, personenWahl, idZuMail]);
  // Mehrtägige Termine stehen an jedem Tag – in „Danach“ nur einmal
  const naechste = [...kommende.values()].flat().filter((e, i, l) => l.findIndex(x => x.id === e.id) === i).slice(0, 5);
  const personFarbe = (t: Termin): ZustaendigFarbe | undefined => {
    const id = terminPersonId(t, idZuMail);
    return id ? farben.get(id) : undefined;
  };

  const blaettern = (d: number) => {
    let m = monat + d; let j = jahr;
    if (m < 1) { m = 12; j -= 1; } if (m > 12) { m = 1; j += 1; }
    setMonat(m); setJahr(j);
  };
  const zuHeute = () => { setJahr(Number(heute.slice(0, 4))); setMonat(Number(heute.slice(5, 7))); setGewaehlt(heute); };
  const waehle = (iso: string) => {
    setGewaehlt(iso);
    setHervorgehoben(null);
    if (Number(iso.slice(5, 7)) !== monat || Number(iso.slice(0, 4)) !== jahr) { setJahr(Number(iso.slice(0, 4))); setMonat(Number(iso.slice(5, 7))); }
    if (window.innerWidth < 1024) window.setTimeout(() => liste.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };

  const gDatum = new Date(`${gewaehlt}T12:00:00`);
  const rundKnopf: React.CSSProperties = { width: 38, height: 38, borderRadius: '50%', border: 'none', background: 'var(--fill2)', color: 'var(--ink)' };

  return (
    <div className="flex flex-col" style={{ gap: 18 }}>
      <header className="flex items-end justify-between flex-wrap" style={{ gap: 14 }}>
        <div className="flex flex-col" style={{ gap: 4 }}>
          <h1 className="m-0" style={{ fontSize: 46, lineHeight: 1.05, fontWeight: 700, letterSpacing: '-0.035em' }}>Kalender</h1>
          <span style={{ fontSize: 14, color: 'var(--ink3)' }}>{laedt ? 'Termine werden geladen …' : personenWahl.size
            ? `Termine von ${teamMembers.filter(p => personenWahl.has(p.id)).map(p => vorname(p.full_name)).join(', ')} · ${termineGefiltert.length} ${termineGefiltert.length === 1 ? 'Termin' : 'Termine'}`
            : kalenderUntertitel(termine)}</span>
        </div>
        <div className="flex flex-wrap" style={{ gap: 8 }}>
          <ImmoButton icon={ArrowsClockwise} onClick={() => { void handleAktualisieren(); }} disabled={syncLaeuft} title="Jetzt mit Outlook abgleichen">
            {syncLaeuft ? 'Gleicht ab …' : 'Outlook abgleichen'}
          </ImmoButton>
          <ImmoButton art="akzent" icon={Plus} onClick={() => terminFormular(null, null)}>Neuer Termin</ImmoButton>
        </div>
      </header>

      <div className="flex flex-wrap items-center" style={{ gap: 8 }}>
        <div role="group" aria-label="Termine nach Art filtern" className="immo-ohne-scrollbar flex overflow-x-auto"
          style={{ padding: 4, gap: 2, borderRadius: 20, background: 'var(--fill2)', maxWidth: '100%' }}>
          {FILTER.map(k => {
            const an = filter === k;
            const farbe = k === 'alle' ? 'var(--ink3)' : KATEGORIE_INFO[k].farbe;
            return (
              <button key={k} type="button" aria-pressed={an} onClick={() => setFilter(k)} className="flex items-center whitespace-nowrap cursor-pointer"
                style={{ height: 36, padding: '0 14px', borderRadius: 16, border: 'none', gap: 7, fontSize: 13, fontWeight: 700,
                  background: an ? 'var(--acc-fill)' : 'transparent', color: an ? 'var(--acc-ink)' : 'var(--ink2)' }}>
                <span aria-hidden style={{ width: 8, height: 8, borderRadius: '50%', background: an && k === 'alle' ? 'var(--acc-ink)' : farbe }} />
                {k === 'alle' ? 'Alle' : KATEGORIE_INFO[k].label}
                {anzahl[k] > 0 && <span style={{ fontSize: 11, opacity: 0.6 }}>{anzahl[k]}</span>}
              </button>
            );
          })}
        </div>
        <button type="button" aria-pressed={zeigeFristen} onClick={toggleFristen} className="flex items-center whitespace-nowrap cursor-pointer"
          style={{ height: 44, padding: '0 14px', borderRadius: 20, border: 'none', gap: 7, fontSize: 13, fontWeight: 700,
            background: zeigeFristen ? 'var(--bad-soft)' : 'var(--fill2)', color: zeigeFristen ? 'var(--bad)' : 'var(--ink3)' }}>
          <Alarm weight={zeigeFristen ? 'fill' : 'regular'} size={15} />Fristen {zeigeFristen ? `an${fristen.length ? ` · ${fristen.length}` : ''}` : 'aus'}
        </button>
      </div>

      {/* Mitarbeiter (Ticket #506): filtern und auf einen Blick sehen, wer gerade im Termin
          oder abwesend ist — z. B. wenn ein Anruf für Björn kommt. Mehrfachauswahl; ohne Auswahl alle. */}
      {teamMembers.length > 0 && (
        <div role="group" aria-label="Termine nach Mitarbeiter filtern" className="immo-ohne-scrollbar flex overflow-x-auto"
          style={{ gap: 8, paddingBottom: 2 }}>
          <button type="button" aria-pressed={personenWahl.size === 0} onClick={() => umschaltePerson(null)}
            className="flex items-center whitespace-nowrap cursor-pointer shrink-0"
            style={{ height: 52, padding: '0 16px', borderRadius: 18, border: 'none', gap: 7, fontSize: 13, fontWeight: 700,
              background: personenWahl.size === 0 ? 'var(--acc-fill)' : 'var(--fill2)', color: personenWahl.size === 0 ? 'var(--acc-ink)' : 'var(--ink2)' }}>
            <UsersThree size={16} />Alle
          </button>
          {teamMembers.map(p => {
            const an = personenWahl.has(p.id);
            const st = jetztStatus(p.id, heuteTermine, heuteAbwEintraege, idZuMail, jetzt);
            return (
              <button key={p.id} type="button" aria-pressed={an} onClick={() => umschaltePerson(p.id)}
                title={`${p.full_name}: ${st.text}`}
                className="flex flex-col items-start justify-center whitespace-nowrap cursor-pointer shrink-0 text-left"
                style={{ height: 52, padding: '0 14px', borderRadius: 18, border: 'none', gap: 2,
                  background: an ? 'var(--acc-fill)' : 'var(--fill2)', color: an ? 'var(--acc-ink)' : 'var(--ink)' }}>
                <span className="flex items-center" style={{ gap: 6, fontSize: 13, fontWeight: 700 }}>
                  {farben.get(p.id) && <span aria-hidden style={{ width: 10, height: 10, borderRadius: 3, background: farben.get(p.id)!.fg }} />}
                  {p.full_name}
                </span>
                <span className="flex items-center" style={{ gap: 5, fontSize: 11, fontWeight: 600, opacity: an ? 0.85 : 1, color: an ? 'inherit' : 'var(--ink3)' }}>
                  <span aria-hidden style={{ width: 7, height: 7, borderRadius: '50%', background: STATUS_FARBE[st.art] }} />
                  {st.text}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {formular && (
        <div ref={formularRef} style={{ scrollMarginTop: 70 }}>
          <TerminFormularKarte key={formular.termin?.id ?? `neu-${formular.prefill ?? ''}`} termin={formular.termin} prefillDate={formular.prefill} teamMembers={teamMembers}
            onAbbrechen={() => setFormular(null)} onGespeichert={() => { setFormular(null); void ladeMonat(); }} />
        </div>
      )}

      {fehler && (
        <div role="alert" className="flex items-center" style={{ gap: 10, padding: '10px 14px', borderRadius: 16, background: 'var(--bad-soft)', color: 'var(--bad)', fontSize: 13, fontWeight: 600 }}>
          <WarningCircle weight="fill" size={18} />Termine konnten nicht geladen werden: {fehler}
        </div>
      )}

      <div className="flex flex-wrap items-start" style={{ gap: 18 }}>
        <section aria-label="Monatsansicht" className="immo-glas flex flex-col" style={{ flex: '2 1 560px', minWidth: 0, borderRadius: 28, padding: 18, gap: 12 }}>
          <div className="flex items-center justify-between flex-wrap" style={{ gap: 10 }}>
            <h2 className="m-0" style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em' }}>{MONATE[monat - 1]} {jahr}</h2>
            <div className="flex items-center" style={{ gap: 6 }}>
              <button type="button" onClick={zuHeute} className="immo-hover-flaeche cursor-pointer" style={{ height: 38, padding: '0 14px', borderRadius: 19, border: 'none', background: 'var(--fill2)', color: 'var(--ink)', fontSize: 13, fontWeight: 700 }}>Heute</button>
              <button type="button" aria-label="Vorheriger Monat" onClick={() => blaettern(-1)} className="immo-hover-flaeche flex items-center justify-center cursor-pointer" style={rundKnopf}><CaretLeft weight="bold" size={15} /></button>
              <button type="button" aria-label="Nächster Monat" onClick={() => blaettern(1)} className="immo-hover-flaeche flex items-center justify-center cursor-pointer" style={rundKnopf}><CaretRight weight="bold" size={15} /></button>
            </div>
          </div>
          <div className="grid" style={{ gridTemplateColumns: 'repeat(7,minmax(0,1fr))', gap: 6 }}>
            {WT_KOPF.map(w => <span key={w} className="text-center" style={{ fontSize: 11, fontWeight: 800, color: 'var(--ink3)', letterSpacing: '0.05em', paddingBottom: 2 }}>{w}</span>)}
            {raster.flat().map(tag => {
              const ev = proTag.get(tag.iso) ?? [];
              const abw = abwJeTag.get(tag.iso) ?? [];
              const platzTermine = Math.max(0, 2 - abw.length);
              const weitere = Math.max(0, abw.length - 2) + Math.max(0, ev.length - platzTermine);
              const istHeute = tag.iso === heute;
              const istGewaehlt = tag.iso === gewaehlt;
              return (
                <button key={tag.iso} type="button" onClick={() => waehle(tag.iso)} aria-pressed={istGewaehlt}
                  aria-label={`${tag.tag}. ${MONATE[Number(tag.iso.slice(5, 7)) - 1]}${ev.length ? `, ${ev.length} ${ev.length === 1 ? 'Eintrag' : 'Einträge'}` : ''}${abw.length ? `, abwesend: ${abw.map(a => vorname(a.name)).join(', ')}` : ''}`}
                  className="immo-hover-flaeche flex flex-col text-left cursor-pointer"
                  style={{ minHeight: 'clamp(54px, 11vw, 96px)', minWidth: 0, borderRadius: 16, padding: 7, gap: 4, border: 'none', color: 'var(--ink)', opacity: tag.imMonat ? 1 : 0.4,
                    background: istGewaehlt ? 'var(--acc-soft)' : 'var(--fill)', boxShadow: istGewaehlt ? 'inset 0 0 0 2px var(--acc)' : 'inset 0 0 0 1px var(--line)' }}>
                  <span className="flex items-center justify-center" style={{ alignSelf: 'flex-start', minWidth: 26, height: 26, padding: '0 6px', borderRadius: 13, fontSize: 13,
                    fontWeight: istHeute || istGewaehlt ? 800 : 600, background: istHeute ? 'var(--acc-fill)' : 'transparent', color: istHeute ? 'var(--acc-ink)' : 'var(--ink)' }}>{tag.tag}</span>
                  {abw.slice(0, 2).map(a => {
                    const f = farben.get(a.mitarbeiterId);
                    return (
                      <span key={`abw-${a.id}`} className="hidden sm:flex items-center" style={{ gap: 4, height: 20, padding: '0 6px', borderRadius: 7, fontSize: 10, fontWeight: 700,
                        background: f?.bg ?? ABWESEND_FLAECHE, color: f?.fg ?? 'var(--ink2)', boxShadow: f ? `inset 3px 0 0 ${f.fg}` : undefined, minWidth: 0 }}>
                        <Airplane aria-hidden size={10} style={{ flexShrink: 0 }} />
                        <span className="truncate" style={{ color: 'var(--ink)' }}>{vorname(a.name)} · {a.text}</span>
                      </span>
                    );
                  })}
                  {ev.slice(0, platzTermine).map(e => {
                    // Farbe des Zuständigen wie im Postfach (#615), sonst die der Art
                    const pf = e.art === 'termin' ? personFarbe(e.termin) : undefined;
                    const farbe = e.art === 'frist' ? FRIST_FARBE : pf?.fg ?? KATEGORIE_INFO[e.kat].farbe;
                    const flaeche = e.art === 'frist' ? 'var(--bad-soft)' : pf?.bg ?? KATEGORIE_INFO[e.kat].flaeche;
                    const sp = e.art === 'termin' ? e.spanne : undefined;
                    if (sp) {
                      // Mehrtägig (Urlaub): durchgehender Balken bis an den Kachelrand wie in Outlook,
                      // Titel am ersten Tag und am Wochenanfang
                      const mitTitel = sp.erster || tag.iso === raster.find(w => w.some(x => x.iso === tag.iso))?.[0]?.iso;
                      return (
                        <span key={e.id} className="hidden sm:flex items-center" title={e.art === 'termin' ? `${e.termin.titel || 'Termin'} · ${zeitLabel(e.termin)}` : undefined}
                          style={{ gap: 4, height: 20, padding: '0 6px', fontSize: 10, fontWeight: 700, background: flaeche, minWidth: 0,
                            marginLeft: sp.erster ? 0 : -7, marginRight: sp.letzter ? 0 : -7,
                            borderRadius: `${sp.erster ? 7 : 0}px ${sp.letzter ? 7 : 0}px ${sp.letzter ? 7 : 0}px ${sp.erster ? 7 : 0}px`,
                            boxShadow: sp.erster ? `inset 3px 0 0 ${farbe}` : undefined }}>
                          <span className="truncate" style={{ visibility: mitTitel ? 'visible' : 'hidden' }}>{e.art === 'termin' ? e.termin.titel || 'Termin' : ''}</span>
                        </span>
                      );
                    }
                    return (
                      <span key={e.id} className="hidden sm:flex items-center" style={{ gap: 4, height: 20, padding: '0 6px', borderRadius: 7, fontSize: 10, fontWeight: 700, background: flaeche, minWidth: 0 }}>
                        <span aria-hidden style={{ width: 5, height: 5, borderRadius: '50%', flexShrink: 0, background: farbe }} />
                        <span className="truncate">{e.art === 'frist' ? e.frist.titel : e.termin.titel || 'Termin'}</span>
                      </span>
                    );
                  })}
                  {weitere > 0 && <span className="hidden sm:block" style={{ fontSize: 10, fontWeight: 800, color: 'var(--ink3)', paddingLeft: 4 }}>+{weitere} weitere</span>}
                  {ev.length > 0 && (
                    <span aria-hidden className="flex sm:hidden flex-wrap" style={{ gap: 3, paddingLeft: 4 }}>
                      {ev.slice(0, 4).map(e => <span key={e.id} style={{ width: 6, height: 6, borderRadius: '50%', background: e.art === 'frist' ? FRIST_FARBE : personFarbe(e.termin)?.fg ?? KATEGORIE_INFO[e.kat].farbe }} />)}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </section>

        <div ref={liste} className="flex flex-col" style={{ flex: '1 1 300px', minWidth: 0, gap: 12, scrollMarginTop: 70 }}>
          <section aria-label="Tagesliste" className="immo-glas flex flex-col" style={{ borderRadius: 28, padding: 18, gap: 12 }}>
            <div className="flex items-center" style={{ gap: 14 }}>
              <div className="flex flex-col items-center justify-center shrink-0" style={{ width: 58, height: 64, borderRadius: 18, background: 'var(--acc-fill)', color: 'var(--acc-ink)' }}>
                <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.06em' }}>{MON_KURZ[gDatum.getMonth()]}</span>
                <span style={{ fontSize: 26, fontWeight: 800, lineHeight: 1 }}>{String(gDatum.getDate()).padStart(2, '0')}</span>
              </div>
              <div className="flex flex-col flex-1 min-w-0" style={{ gap: 2 }}>
                <span style={{ fontSize: 18, fontWeight: 800 }}>{WOCHENTAGE[gDatum.getDay()]}, {gDatum.getDate()}. {MONATE[gDatum.getMonth()]}</span>
                <span style={{ fontSize: 13, color: 'var(--ink3)' }}>{tagesEintraege.length ? `${tagesEintraege.length} ${tagesEintraege.length === 1 ? 'Eintrag' : 'Einträge'}` : 'Keine Termine'}</span>
              </div>
              <button type="button" aria-label="Termin an diesem Tag anlegen" title="Termin an diesem Tag anlegen" onClick={() => terminFormular(null, gewaehlt)}
                className="immo-hover-flaeche flex items-center justify-center cursor-pointer" style={rundKnopf}><CalendarPlus size={17} /></button>
            </div>
            {tagesAbwesend.length > 0 && (
              <div className="flex flex-col" style={{ gap: 6, padding: 12, borderRadius: 18, background: ABWESEND_FLAECHE }}>
                <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--ink3)' }}>Abwesend</span>
                {tagesAbwesend.map(a => (
                  <span key={a.id} className="flex items-center" style={{ gap: 8, fontSize: 13, fontWeight: 600 }}>
                    <Airplane aria-hidden size={14} color={farben.get(a.mitarbeiterId)?.fg ?? 'var(--ink3)'} />
                    {a.name}<span style={{ color: 'var(--ink3)', fontWeight: 500 }}>· {a.halberTag ? `halber Tag ${a.text}` : a.text}</span>
                  </span>
                ))}
              </div>
            )}
            {tagesEintraege.map(e => e.art === 'frist'
              ? <FristKarte key={e.id} f={e.frist} onOeffnen={() => fristOeffnen(e.frist)} />
              : <TerminKarte key={e.id} e={e} ticket={ticketProTermin[e.id]} hervorgehoben={hervorgehoben === e.id} personFarbe={personFarbe(e.termin)}
                  onBearbeiten={() => terminFormular(e.termin, null)} onLoeschen={() => setLoeschen(e.termin)}
                  onObjekt={onNavigateToObjekt} onMieter={onNavigateToMieter} onEigentuemer={onNavigateToEigentuemer} onTicket={onNavigateToTicket} />)}
            {tagesEintraege.length === 0 && (
              <div className="flex flex-col items-center text-center" style={{ borderRadius: 18, padding: 22, gap: 8, background: 'var(--fill)' }}>
                <CalendarBlank weight="duotone" size={32} color="var(--ink3)" />
                <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Keine Termine an diesem Tag.</span>
                <ImmoButton groesse={36} icon={Plus} onClick={() => terminFormular(null, gewaehlt)}>Termin anlegen</ImmoButton>
              </div>
            )}
          </section>
          <section aria-label="Danach" className="immo-glas flex flex-col" style={{ borderRadius: 28, padding: 18, gap: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--ink3)' }}>Danach</span>
            {naechste.length === 0 && <span style={{ fontSize: 13, color: 'var(--ink3)', padding: 8 }}>Keine weiteren Termine.</span>}
            {naechste.map(e => {
              if (e.art !== 'termin') return null;
              const d = new Date(`${e.tag}T12:00:00`);
              return (
                <button key={e.id} type="button" onClick={() => waehle(e.tag)} className="immo-hover-flaeche flex items-center text-left cursor-pointer"
                  style={{ gap: 12, padding: 8, borderRadius: 14, border: 'none', background: 'transparent', color: 'var(--ink)', minWidth: 0 }}>
                  <span className="flex flex-col items-center shrink-0" style={{ width: 42 }}>
                    <span style={{ fontSize: 10, fontWeight: 800, color: KATEGORIE_INFO[e.kat].farbe }}>{MON_KURZ[d.getMonth()]}</span>
                    <span style={{ fontSize: 19, fontWeight: 800, lineHeight: 1.05 }}>{String(d.getDate()).padStart(2, '0')}</span>
                  </span>
                  <span className="flex flex-col flex-1 min-w-0">
                    <span className="truncate" style={{ fontSize: 13, fontWeight: 700 }}>{e.termin.titel || 'Termin'}</span>
                    <span className="truncate" style={{ fontSize: 11, color: 'var(--ink3)' }}>{e.termin.objekt?.bezeichnung || terminTypLabel(e.termin)}</span>
                  </span>
                </button>
              );
            })}
          </section>
        </div>
      </div>

      {loeschen && (
        <BestaetigenDialog titel="Termin löschen?" text="Der Termin wird auch in Outlook gelöscht. Das kann nicht rückgängig gemacht werden."
          detail={<>„{loeschen.titel}“<br /><span style={{ fontSize: 12, color: 'var(--ink3)' }}>{formatDatum(loeschen.start_datum)} · {zeitLabel(loeschen)}</span></>}
          bestaetigenLabel="Löschen" laeuft={loeschtGerade} onBestaetigen={() => { void bestaetigeLoeschen(); }} onAbbrechen={() => setLoeschen(null)} />
      )}
    </div>
  );
};

function Zeile({ icon: IconKomp, children, onClick, titel }: { icon: typeof Buildings; children: React.ReactNode; onClick?: () => void; titel?: string }) {
  const inhalt = <><IconKomp size={14} style={{ flexShrink: 0 }} /><span className="truncate">{children}</span></>;
  return onClick ? (
    <button type="button" onClick={onClick} title={titel} className="flex items-center text-left cursor-pointer min-w-0"
      style={{ gap: 7, border: 'none', background: 'transparent', padding: 0, fontSize: 12, fontWeight: 700, color: 'var(--ink)' }}>{inhalt}</button>
  ) : <span className="flex items-center min-w-0" style={{ gap: 7, fontSize: 12, color: 'var(--ink2)' }}>{inhalt}</span>;
}

function TerminKarte({ e, ticket, hervorgehoben, personFarbe, onBearbeiten, onLoeschen, onObjekt, onMieter, onEigentuemer, onTicket }: {
  e: Extract<KalenderEintrag<Termin>, { art: 'termin' }>;
  personFarbe?: ZustaendigFarbe;
  ticket?: { id: string; ticket_nummer: string | null; titel: string | null };
  hervorgehoben: boolean;
  onBearbeiten: () => void;
  onLoeschen: () => void;
  onObjekt?: (id: string) => void;
  onMieter?: (id: string) => void;
  onEigentuemer?: (id: string) => void;
  onTicket?: (id: string) => void;
}) {
  const t = e.termin;
  const info = KATEGORIE_INFO[e.kat];
  const mieter = personName(t.mieter_rel);
  const eigentuemer = personName(t.eigentuemer_rel);
  const abgesagt = t.status === 'abgesagt';
  const [langOffen, setLangOffen] = useState(false);
  const textLang = `${t.beschreibung ?? ''}${t.notizen ?? ''}`.length > 180 || /\n.*\n.*\n/.test(`${t.beschreibung ?? ''}\n${t.notizen ?? ''}`);
  return (
    <article id={`termin-${t.id}`} className="flex flex-col min-w-0"
      style={{ borderRadius: 20, padding: 14, gap: 10, background: 'var(--fill)', opacity: abgesagt ? 0.6 : 1,
        boxShadow: hervorgehoben ? 'inset 0 0 0 2px var(--acc)' : 'inset 0 0 0 1px var(--line)' }}>
      <div className="flex items-center min-w-0" style={{ gap: 10 }}>
        <span aria-hidden style={{ width: 4, alignSelf: 'stretch', borderRadius: 2, background: personFarbe?.fg || t.farbe || info.farbe }} />
        <div className="flex flex-col flex-1 min-w-0" style={{ gap: 2 }}>
          <span style={{ fontSize: 15, fontWeight: 800, overflowWrap: 'anywhere', textDecoration: abgesagt ? 'line-through' : undefined }}>{t.titel || 'Termin'}</span>
          <span className="flex items-center" style={{ gap: 5, fontSize: 12, color: 'var(--ink3)' }}>
            <Clock size={13} />{zeitLabel(t)}
            {t.outlook_event_id && <span className="flex items-center" title="Mit Outlook abgeglichen" style={{ gap: 3 }}><MicrosoftOutlookLogo size={13} />Outlook</span>}
          </span>
        </div>
        <span className="flex items-center shrink-0" style={{ height: 24, padding: '0 9px', borderRadius: 12, gap: 4, fontSize: 11, fontWeight: 800, background: info.flaeche, color: info.farbe }}>
          {e.kat === 'sonstige' ? terminTypLabel(t) : info.label}
        </span>
      </div>
      <div className="flex flex-col" style={{ gap: 6 }}>
        {t.objekt && <Zeile icon={Buildings} onClick={onObjekt ? () => onObjekt(t.objekt!.id) : undefined} titel="Zum Objekt">{t.objekt.bezeichnung}</Zeile>}
        {mieter && t.mieter_rel && <Zeile icon={User} onClick={onMieter ? () => onMieter(t.mieter_rel!.id) : undefined} titel="Mieter öffnen">{mieter}</Zeile>}
        {eigentuemer && t.eigentuemer_rel && <Zeile icon={Crown} onClick={onEigentuemer ? () => onEigentuemer(t.eigentuemer_rel!.id) : undefined} titel="Eigentümer öffnen">{eigentuemer}</Zeile>}
        {t.zustaendig && (personFarbe
          ? <span className="flex items-center min-w-0" style={{ gap: 7, fontSize: 12, color: 'var(--ink2)' }}>
              <UserCircle size={14} style={{ flexShrink: 0 }} />Zuständig:
              <span className="truncate" style={{ padding: '2px 8px', borderRadius: 9, fontWeight: 700, background: personFarbe.bg, color: personFarbe.fg }}>{t.zustaendig.full_name}</span>
            </span>
          : <Zeile icon={UserCircle}>Zuständig: {t.zustaendig.full_name}</Zeile>)}
        {t.status && t.status !== 'geplant' && <Zeile icon={CalendarBlank}>{STATUS_LABELS[t.status] || t.status}</Zeile>}
        {ticket && <Zeile icon={Ticket} onClick={onTicket ? () => onTicket(ticket.id) : undefined} titel="Zum Ticket">{`Ticket ${ticket.ticket_nummer ?? ''} ${ticket.titel ?? ''}`.trim()}</Zeile>}
      </div>
      {(t.beschreibung || t.notizen) && (
        <div className="flex flex-col items-start" style={{ gap: 4 }}>
          <div className="flex flex-col" style={{ gap: 4, fontSize: 12, color: 'var(--ink2)', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere',
            ...(langOffen ? {} : { display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' as const, overflow: 'hidden' }) }}>
            {[t.beschreibung, t.notizen].filter(Boolean).join('\n\n')}
          </div>
          {textLang && (
            <button type="button" onClick={() => setLangOffen(o => !o)} aria-expanded={langOffen} className="cursor-pointer"
              style={{ border: 'none', background: 'transparent', padding: 0, fontSize: 12, fontWeight: 800, color: 'var(--acc)' }}>
              {langOffen ? 'Weniger anzeigen' : 'Mehr anzeigen'}
            </button>
          )}
        </div>
      )}
      {t.teilnehmer && t.teilnehmer.length > 0 && (
        <div className="flex flex-col" style={{ gap: 4 }}>
          <span className="flex items-center" style={{ gap: 6, fontSize: 11, fontWeight: 800, color: 'var(--ink3)', textTransform: 'uppercase', letterSpacing: '0.05em' }}><UsersThree size={13} />Teilnehmer</span>
          {t.teilnehmer.map(p => {
            const st = TEILNAHME[p.status ?? 'none'] ?? TEILNAHME.none!;
            return (
              <span key={p.email} className="flex items-center justify-between" style={{ gap: 8, fontSize: 12 }}>
                <span className="truncate">{p.name || p.email}</span>
                <span className="shrink-0" style={{ fontWeight: 800, color: st.farbe }}>{st.text}</span>
              </span>
            );
          })}
        </div>
      )}
      <div className="flex flex-wrap" style={{ gap: 6 }}>
        <ImmoButton groesse={36} icon={PencilSimple} onClick={onBearbeiten}>Bearbeiten</ImmoButton>
        {t.objekt && onObjekt && <ImmoButton groesse={36} icon={ArrowRight} onClick={() => onObjekt(t.objekt!.id)}>Zum Objekt</ImmoButton>}
        <ImmoButton groesse={36} art="bad" icon={Trash} aria-label="Termin löschen" title="Termin löschen" onClick={onLoeschen} />
      </div>
    </article>
  );
}

function FristKarte({ f, onOeffnen }: { f: FristEintrag; onOeffnen: () => void }) {
  return (
    <article className="flex flex-col min-w-0" style={{ borderRadius: 20, padding: 14, gap: 10, background: 'var(--fill)', boxShadow: 'inset 0 0 0 1px var(--line)' }}>
      <div className="flex items-center min-w-0" style={{ gap: 10 }}>
        <span aria-hidden style={{ width: 4, alignSelf: 'stretch', borderRadius: 2, background: FRIST_FARBE }} />
        <div className="flex flex-col flex-1 min-w-0" style={{ gap: 2 }}>
          <span style={{ fontSize: 15, fontWeight: 800, overflowWrap: 'anywhere' }}>{f.titel}</span>
          {f.untertitel && <span className="truncate" style={{ fontSize: 12, color: 'var(--ink3)' }}>{f.untertitel}</span>}
        </div>
        <Chip groesse="xs" ton="bad" icon={Alarm}>{f.ueberfaellig ? 'Überfällig' : 'Frist'}</Chip>
      </div>
      <ImmoButton groesse={36} icon={LinkIcon} onClick={onOeffnen} style={{ alignSelf: 'flex-start' }}>
        {f.ziel.typ === 'rechnung' ? 'Rechnung öffnen' : f.ziel.typ === 'ticket' ? 'Ticket öffnen' : 'E-Mail öffnen'}
      </ImmoButton>
    </article>
  );
}

export default KalenderView;
